import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FaMapMarkerAlt, FaCalendarAlt, FaClock, FaTimes, FaGraduationCap, FaChevronRight } from 'react-icons/fa';
import API_BASE_URL, { ASSET_BASE_URL } from '../../config/api';

const StatsSection = () => {
  const [institutes, setInstitutes] = useState([]);
  const [selectedInstitute, setSelectedInstitute] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetch(`${API_BASE_URL}/get_institutes.php`)
      .then(res => res.json())
      .then(res => {
        if (res.status === 'success') {
          setInstitutes(res.data);
        }
        setLoading(false);
      })
      .catch(err => {
        console.error(err);
        setLoading(false);
      });
  }, []);

  const openPortal = (institute) => {
    setSelectedInstitute(institute);
    setIsModalOpen(true);
  };

  if (loading) return null;

  const getImageUrl = (path) => {
    if (!path) return '';
    if (path.startsWith('http')) return path;
    return `${ASSET_BASE_URL}/${path.startsWith('/') ? path.slice(1) : path}`;
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    show: {
      opacity: 1,
      transition: { staggerChildren: 0.1 }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    show: { opacity: 1, y: 0, transition: { type: 'spring', stiffness: 300, damping: 24 } }
  };

  return (
    <>
      <section className="max-w-7xl mx-auto mb-20 px-4 relative z-20">
        <motion.div 
          variants={containerVariants}
        initial="hidden"
        whileInView="show"
        viewport={{ once: true, margin: "-50px" }}
        className="flex flex-wrap justify-center gap-6 lg:gap-8"
      >
        {institutes.map((inst) => (
          <motion.div 
            key={inst.id} 
            variants={itemVariants}
            className="w-full sm:w-[calc(50%-1.5rem)] lg:w-[calc(33.333%-1.5rem)] max-w-[320px] group relative bg-white border border-slate-100 rounded-[2rem] p-6 text-center shadow-[0_8px_30px_rgb(0,0,0,0.04)] hover:shadow-[0_20px_40px_rgba(249,115,22,0.1)] transition-all duration-500 flex flex-col overflow-hidden"
          >
            {/* Top accent gradient line */}
            <div className="absolute top-0 left-0 right-0 h-1.5 bg-gradient-to-r from-orange-400 to-red-500 opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>

            <div className="relative z-10 flex flex-col items-center flex-grow">
              <div className="relative w-24 h-24 mx-auto rounded-2xl flex items-center justify-center mb-6 bg-gradient-to-br from-orange-50 to-red-50 border border-orange-100/50 group-hover:scale-105 transition-transform duration-500 shadow-inner">
                {inst.logo_url ? (
                  <img src={getImageUrl(inst.logo_url)} alt={inst.name} className="w-16 h-16 object-contain filter drop-shadow-sm" />
                ) : (
                  <div className="text-orange-500 font-bold text-3xl">{inst.name.charAt(0)}</div>
                )}
                <div className="absolute -inset-2 bg-gradient-to-r from-orange-400/20 to-red-400/20 rounded-2xl blur-xl opacity-0 group-hover:opacity-100 transition-opacity duration-500 -z-10"></div>
              </div>
              
              <h3 className="text-xl font-black text-slate-800 tracking-tight mb-2 uppercase group-hover:text-transparent group-hover:bg-clip-text group-hover:bg-gradient-to-r group-hover:from-orange-600 group-hover:to-red-600 transition-all duration-300">
                {inst.name}
              </h3>
              
              <div className="flex items-center gap-1.5 text-slate-500 mb-4 bg-slate-50 px-3 py-1 rounded-full text-xs font-semibold uppercase tracking-wider">
                <FaMapMarkerAlt className="text-orange-500 flex-shrink-0" />
                <span className="truncate max-w-[150px]">{inst.location}</span>
              </div>
              
              <p className="text-slate-500 text-sm leading-relaxed mb-8 font-medium">
                Access academic year schedules, timetables, and campus updates.
              </p>
            </div>
            
            <button 
              onClick={() => openPortal(inst)}
              className="mt-auto relative overflow-hidden w-full bg-slate-900 text-white font-bold text-xs py-4 rounded-xl tracking-widest uppercase transition-all duration-300 hover:shadow-lg flex items-center justify-center gap-2 group/btn"
            >
              <div className="absolute inset-0 bg-gradient-to-r from-orange-500 to-red-600 translate-y-full group-hover/btn:translate-y-0 transition-transform duration-300 ease-in-out"></div>
              <span className="relative z-10 flex items-center gap-2">
                Open Portal 
                <FaChevronRight className="text-[10px] group-hover/btn:translate-x-1 transition-transform duration-300" />
              </span>
            </button>
          </motion.div>
        ))}
      </motion.div>
      </section>

      {/* Modal */}
      <AnimatePresence>
        {isModalOpen && selectedInstitute && (
          <motion.div 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[9999] flex items-center justify-center p-4 sm:p-6 bg-slate-900/40 backdrop-blur-sm"
            onClick={() => setIsModalOpen(false)}
          >
            <motion.div 
              initial={{ scale: 0.95, opacity: 0, y: 20 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.95, opacity: 0, y: 20 }}
              transition={{ type: 'spring', damping: 25, stiffness: 300 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-white rounded-[2rem] w-full max-w-3xl max-h-[90vh] flex flex-col overflow-hidden shadow-2xl relative"
            >
              {/* Modal Header */}
              <div className="flex-shrink-0 p-6 sm:p-8 bg-gradient-to-r from-orange-500 to-red-600 rounded-t-[2rem] relative flex justify-between items-start">
                <div>
                  <h2 className="text-2xl sm:text-[28px] font-black italic text-white tracking-wide uppercase leading-tight">
                    {selectedInstitute.name} TIMETABLE
                  </h2>
                  <p className="font-medium italic text-orange-100 text-base mt-1">
                    {selectedInstitute.location}
                  </p>
                </div>
                
                <button 
                  onClick={() => setIsModalOpen(false)}
                  className="w-10 h-10 bg-white/20 hover:bg-white/30 text-white rounded-xl flex items-center justify-center transition-colors flex-shrink-0"
                >
                  <FaTimes className="text-lg" />
                </button>
              </div>

              {/* Modal Body */}
              <div className="flex-1 overflow-y-auto custom-scrollbar p-4 sm:p-6 bg-[#f8fafc]">
                
                {/* Mobile Cards Layout (Hidden on md+ screens) */}
                <div className="space-y-4 md:hidden">
                  {selectedInstitute.schedules && selectedInstitute.schedules.length > 0 ? (
                    selectedInstitute.schedules.map((sched) => (
                      <motion.div 
                        key={sched.id}
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="bg-white rounded-2xl border border-orange-100/50 p-5 shadow-sm"
                      >
                        <div className="flex justify-between items-center mb-4">
                          <div className="bg-gradient-to-r from-orange-500 to-red-600 text-white font-black text-[11px] sm:text-xs px-4 py-2 rounded-xl tracking-wider uppercase">
                            {sched.class_segment}
                          </div>
                          <div className="font-black text-slate-900 text-[13px] sm:text-sm tracking-widest uppercase">
                            {sched.class_day}
                          </div>
                        </div>
                        <div className="flex items-center gap-3 px-1">
                          <div className="w-2.5 h-2.5 rounded-full bg-orange-400 shadow-[0_0_8px_rgba(249,115,22,0.5)]"></div>
                          <div className="font-bold text-slate-700 text-base sm:text-lg tracking-tight">
                            {sched.time_window}
                          </div>
                        </div>
                      </motion.div>
                    ))
                  ) : (
                    <div className="py-16 text-center bg-white rounded-[1rem] border border-slate-100 shadow-sm">
                      <div className="flex flex-col items-center justify-center text-slate-400 space-y-3">
                        <FaCalendarAlt className="text-3xl text-slate-300" />
                        <p className="text-sm font-medium">No schedules available.</p>
                      </div>
                    </div>
                  )}
                </div>

                {/* Desktop Table Layout (Visible on md+ screens) */}
                <div className="hidden md:block bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden">
                  <div className="overflow-x-auto">
                    <table className="w-full text-left border-collapse min-w-[500px]">
                      <thead>
                        <tr className="bg-slate-50/80 border-b border-slate-100">
                          <th className="px-6 py-4 text-[10px] font-black tracking-widest text-slate-400 uppercase">Session Code</th>
                          <th className="px-6 py-4 text-[10px] font-black tracking-widest text-slate-400 uppercase">Class Details</th>
                          <th className="px-6 py-4 text-[10px] font-black tracking-widest text-slate-400 uppercase text-right">Schedule Window</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-50">
                        {selectedInstitute.schedules && selectedInstitute.schedules.length > 0 ? (
                          selectedInstitute.schedules.map((sched) => (
                            <tr key={sched.id} className="hover:bg-orange-50/30 transition-colors group">
                              <td className="px-6 py-5">
                                <span className="inline-flex items-center px-2.5 py-1 rounded-md text-xs font-bold bg-slate-100 text-slate-600 group-hover:bg-white group-hover:shadow-sm transition-all font-mono border border-slate-200/50">
                                  {sched.session_code || '#CLASS'}
                                </span>
                              </td>
                              <td className="px-6 py-5">
                                <div className="font-bold text-slate-800 text-sm mb-1">{sched.class_segment}</div>
                                <div className="flex items-center gap-1.5 text-[10px] font-black text-orange-500 tracking-wider uppercase">
                                  <FaCalendarAlt />
                                  {sched.class_day}
                                </div>
                              </td>
                              <td className="px-6 py-5 text-right">
                                <div className="inline-flex items-center gap-2 bg-slate-900 text-white px-3 py-1.5 rounded-lg text-xs font-bold shadow-sm">
                                  <FaClock className="text-orange-400" />
                                  {sched.time_window}
                                </div>
                              </td>
                            </tr>
                          ))
                        ) : (
                          <tr>
                            <td colSpan="3" className="px-6 py-16 text-center">
                              <div className="flex flex-col items-center justify-center text-slate-400 space-y-3">
                                <div className="w-12 h-12 bg-slate-50 rounded-full flex items-center justify-center">
                                  <FaCalendarAlt className="text-2xl text-slate-300" />
                                </div>
                                <p className="text-sm font-medium">No schedules available for this portal yet.</p>
                              </div>
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>

              </div>

              {/* Modal Footer */}
              <div className="flex-shrink-0 p-4 sm:p-6 bg-white rounded-b-[2rem] border-t border-slate-50">
                <button 
                  onClick={() => setIsModalOpen(false)}
                  className="w-full bg-[#f1f5f9] hover:bg-slate-200 text-slate-700 font-black italic text-[15px] py-4 rounded-2xl tracking-[0.2em] transition-all active:scale-[0.98]"
                >
                  CLOSE PORTAL
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default StatsSection;
